<template>
  <div>
    <RevCard v-if="hasAddresses" class="p-24 md:p-32">
      <header class="mb-32 flex items-center justify-between">
        <div>
          <h2 class="heading-2 mb-8">{{ i18n(translations.title) }}</h2>
          <h3 class="body-1">
            {{ i18n(translations.introduction) }}
          </h3>
        </div>
        <!--
          Because of how CSS is applied in production,
          we can't hide the DS component directly on the component,
          so let's hide it from a wrapper.
        -->
        <div class="hidden md:flex">
          <RevButton variant="primary" @click="handleAddressCreate">
            {{ i18n(translations.createAddress) }}
          </RevButton>
        </div>

        <div class="md:hidden">
          <RevButtonIcon
            :aria-label="i18n(translations.createAddress)"
            :icon="IconPlus"
            variant="primary"
            @click="handleAddressCreate"
          />
        </div>
      </header>

      <main>
        <ul class="grid grid-cols-1 gap-16 md:grid-cols-2 lg:grid-cols-3">
          <AddressCard
            v-for="address in addresses"
            :key="address.id"
            :address
            :data-test="address.id"
          >
            <template #actions>
              <div class="mt-24 flex flex-wrap gap-8">
                <RevButton
                  size="small"
                  variant="secondary"
                  @click="handleAddressEdit(address)"
                >
                  {{ i18n(translations.edit) }}
                </RevButton>

                <DeleteAddressModal
                  v-if="canDeleteAddress"
                  :address-id="address.id"
                  class="inline-block"
                  @address-deleted="handleAddressDeleted"
                >
                  <template #trigger="{ open }">
                    <RevButton size="small" variant="secondary" @click="open">
                      {{ i18n(translations.delete) }}
                    </RevButton>
                  </template>
                </DeleteAddressModal>

                <RevButton
                  v-if="!address.default"
                  size="small"
                  variant="secondary"
                  @click="handleSetDefaultAddress(address)"
                >
                  {{ i18n(translations.setDefault) }}
                </RevButton>
              </div>
            </template>
          </AddressCard>
        </ul>
      </main>

      <Pagination
        class="mt-24 flex"
        :current-page
        :page-count
        @click="handlePaginationClick"
      />
    </RevCard>

    <EmptyState
      v-if="!hasAddresses"
      @open-create-address-form-modal="handleAddressCreate"
    />

    <RevDrawer
      :name="MODAL_NAMES.CREATE_OR_UPDATE"
      :title="i18n(translations.createAddressModalTitle)"
    >
      <template #body="{ close }">
        <AddressForm
          :address="addressToEdit"
          @address-submitted="handleAddressSubmitted(close)"
        />
      </template>
    </RevDrawer>

    <ModalOnboarding
      :button-label="i18n(translations.onboardingModalButtonLabel)"
      :title="i18n(translations.onboardingModalTitle)"
    >
      <template #description>
        {{ i18n(translations.onboardingModalDescription) }}
      </template>
      <template #to-do>
        {{ i18n(translations.onboardingModalToDoSection) }}
      </template>
      <template #done>
        {{ i18n(translations.onboardingModalDoneSection) }}
      </template>
    </ModalOnboarding>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import { openModal } from '@ds/components/ModalBase'
import { IconPlus } from '@ds/icons/IconPlus'

import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  MODAL_NAMES,
} from './AddressesManager.constants'
import translations from './AddressesManager.translations'
import AddressCard from './components/AddressCard/AddressCard.vue'
import AddressForm from './components/AddressForm/AddressForm.vue'
import DeleteAddressModal from './components/DeleteAddressModal/DeleteAddressModal.vue'
import EmptyState from './components/EmptyState/EmptyState.vue'

const i18n = useI18n()
const route = useRoute()

const addressToEdit = ref<merchantsAPI.Address | null>(null)

const currentPage = computed(
  () => parseInt(route.query.page?.toString() || '', 10) || DEFAULT_PAGE,
)

const { data, error, refresh } = await useHttpFetch(merchantsAPI.getAddresses, {
  queryParams: {
    page: currentPage,
    'page-size': DEFAULT_PAGE_SIZE,
  },
  watch: [currentPage],
})

if (error.value) {
  // @TODO
}

const addresses = computed(() => data?.value?.results || [])
const addressesCount = computed(() => data.value?.count || 0)
const hasAddresses = computed(() => addressesCount.value > 0)
const canDeleteAddress = computed(() => addressesCount.value > 1)
const pageCount = computed(() =>
  Math.ceil(addressesCount.value / DEFAULT_PAGE_SIZE),
)

function handleAddressCreate() {
  addressToEdit.value = null
  openModal(MODAL_NAMES.CREATE_OR_UPDATE)
}

function handleAddressEdit(address: merchantsAPI.Address) {
  addressToEdit.value = address
  openModal(MODAL_NAMES.CREATE_OR_UPDATE)
}

function handleAddressSubmitted(close: () => void) {
  refresh()
  close()
}

function handleAddressDeleted() {
  refresh()
}

async function handleSetDefaultAddress(address: merchantsAPI.Address) {
  try {
    await $httpFetch(merchantsAPI.postSetDefaultAddress, {
      pathParams: { id: address.id },
    })
    refresh()
  } catch {
    // fails silently
  }
}

async function handlePaginationClick({ page }: { page: number }) {
  await navigateTo({ ...route, query: { page } })
}
</script>
